// src/lib/kinema/konva/useKeyboardShortcuts.ts
import { useEffect } from 'react';
import type { Editor } from '../core/Editor';
import type { ToolManager } from '../core/tools/ToolManager';
import type { KeyMap } from '../core/tools/KeyMap';
import { defaultKeymap, builtInActions, resolveKeyCombo } from '../core/tools/KeyMap';

export function useKeyboardShortcuts(
  editor: Editor,
  options?: { toolManager?: ToolManager | null; overrides?: Partial<KeyMap> },
): void {
  const toolManager = options?.toolManager;
  const overrides = options?.overrides;

  useEffect(() => {
    const keymap = Object.assign({}, defaultKeymap, overrides) as KeyMap;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't fire shortcuts when typing in an input or textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const combo = resolveKeyCombo(e);
      const actionName = keymap[combo];
      if (!actionName) return;

      const action = builtInActions[actionName];
      if (!action) return;

      e.preventDefault();
      action(editor, toolManager ?? undefined);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editor, toolManager, overrides]);
}
